import type { FC } from 'react';
import type { ProjectData, CaseStudyProps } from '../data-projects';

export const Vegetoil: FC<ProjectData & CaseStudyProps> = ({ cta }) => {
  return (
    <>
      <section>
        <h3>Manual inventory tracking creates 40+ hours of weekly inefficiencies</h3>
        <h4>The Push for Change</h4>
        <p>
          Vegetoil processes hundreds of used cooking oil barrels weekly for biofuel production, but manual tracking systems were creating significant operational bottlenecks. Workers spent excessive time on data entry and barrel location updates, limiting the company's ability to scale operations efficiently.
        </p>
        <p>
          The labor-intensive inventory management process was prone to human error, creating quality control risks and operational delays. With growing market demands for sustainable biofuels, Vegetoil needed digital transformation to maintain competitive advantage.
        </p>
      </section>

      <section id="solution">
        <h3>Digital automation eliminates manual bottlenecks across supply chain</h3>
        <h4>Shaping Solutions from Technology</h4>
        <p>
          A mobile application addressed three critical challenges at once: QR codes at supply points let drivers verify the correct collection location even with poor network connectivity, NFC tags on individual barrels provided real-time status tracking throughout the supply chain, and each driver received their daily itinerary directly in the app, ordered for the optimal route. Together, these eliminated location errors, manual data entry, and the guesswork of paper-based dispatching.
        </p>
        <p>
          A custom ERP tied all operational components together (tracking, quality monitoring, and audit trails) into a single supervisor view, with the data accuracy required for biofuel regulatory compliance. Built into it, an intervention planning feature automates daily dispatching by generating driver routes and itineraries from barrel status, supply point priority, and capacity, removing the manual scheduling that previously gated each morning.
        </p>
      </section>

      <section id="results">
        <h3>72% error reduction drives operational excellence and growth readiness</h3>
        <h4>Delivering Lasting Results</h4>
        <p>
          Vegetoil achieved measurable productivity improvements: 66% faster inventory tracking, 72% reduction in human errors, and 90% decrease in manual reporting time. Barrel information access improved from minutes to an average of 10 seconds, enabling real-time decision-making.
        </p>

        <p>
          The automation freed workforce capacity for value-adding activities while establishing scalable infrastructure for future growth. Vegetoil gained competitive positioning for expanding market opportunities in renewable energy.
        </p>

        <figure>
          <blockquote>
            Keeping track of our assets in the field used to be a constant struggle, and I was spending hours every week planning collections myself, managing missed visit and schedule was a nightmare. Now every barrel is accounted for in and the routes come straight out of the system.
            &#8221;
          </blockquote>
          <figcaption>Jean Jacques Del Maestro, President — Vegetoil</figcaption>
        </figure>
      </section>

      {cta}
    </>
  );
};
